import React from "react";

/**
 * The colour language shared by both AMC dashboard cards.
 *
 * Kept in one place on purpose: the two cards answer different questions (a service is
 * pending / a contract needs renewing) but they sit next to each other, and if red meant
 * "very late" on one and "expiring soon" on the other the dashboard would be actively
 * misleading. Severity runs the same way in both: blue is fine, amber is now, orange is
 * late, red is a problem.
 *
 * The BANDS themselves are decided server-side from the admin's day settings - nothing here
 * knows or guesses a threshold. This file only decides what each band looks like.
 */
export const URGENCY = {
  // Services
  UPCOMING: { color: "#0EA5E9", label: "Upcoming" },
  DUE: { color: "#F59E0B", label: "Due today" },
  OVERDUE: { color: "#EA580C", label: "Overdue" },
  CRITICAL: { color: "#B91C1C", label: "Critical" },

  // Renewals - same scale, so the eye reads both cards the same way.
  DUE_SOON: { color: "#F59E0B", label: "Expiring soon" },
  EXPIRES_TODAY: { color: "#EA580C", label: "Expires today" },
  EXPIRED: { color: "#B91C1C", label: "Expired" },
};

export const fmtDate = (v) =>
  v
    ? new Date(`${String(v).substring(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

/**
 * The coloured bar down the left of a row. This is the classification the whole card is
 * built around, so it is a solid block of the band colour rather than a tinted background -
 * it has to be readable at a glance from across a desk, which is the entire brief.
 */
export const UrgencyStrip = ({ color }) => (
  <div
    style={{
      width: 6,
      borderRadius: 3,
      backgroundColor: color,
      flexShrink: 0,
    }}
  />
);

/**
 * The count-per-band row that sits above the list, so the shape of the problem is legible
 * before a single row is read. Bands with nothing in them are left out rather than shown as
 * zero - a row of zeroes is noise, and their absence is already the good news.
 */
export const UrgencySummary = ({ rows, field, order }) => {
  const counts = order
    .map((key) => ({ key, band: URGENCY[key], count: rows.filter((r) => r[field] === key).length }))
    .filter((c) => c.count > 0);

  if (counts.length === 0) return null;

  return (
    <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
      {counts.map(({ key, band, count }) => (
        <span
          key={key}
          className="d-inline-flex align-items-center fw-semibold"
          style={{
            gap: 6,
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: 999,
            color: band.color,
            backgroundColor: `${band.color}1A`, // same hue at 10% - readable in light and dark
            border: `1px solid ${band.color}40`,
          }}
        >
          <span
            style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: band.color, display: "inline-block" }}
          />
          {count} {band.label}
        </span>
      ))}
    </div>
  );
};
